// Presentation only. LoyaltyLion owns balances, reward eligibility and redemption.
import { initLoyaltySubscriberStatus } from './loyalty-subscriber-status.js?v=20261014-public-held-1';
import { initLoyaltyBonusActivity } from './loyalty-bonus-activity.js?v=20261027-library-purchase-2';

initLoyaltySubscriberStatus();
import { createShopifyIssuedVoucherAdapter } from './loyalty-cart-shopify-adapter.js?v=m5-20261002-stacking';
import {
  createIssuedVoucherHistoryAuthorizer,
  createIssuedVoucherApplyController,
  mountIssuedVoucherApplyAction,
  removeIssuedVoucherApplyAction,
} from './loyalty-provider-issued-apply.js?v=20261002-issued-apply-3';
import { initLoyaltyModalMotion } from './loyalty-provider-modal-motion.js?v=20261009-birthday-popover';
import { rewardExpiryCopy } from './loyalty-voucher-expiry-copy.js?v=20261003-provider-expiry';
import { initLoyaltyFaqMotion } from './loyalty-faq-motion.js?v=20260930-measured-1';
import {
  initLoyaltyClaimedRewards,
  readReadyCashRewardByCode,
} from './loyalty-claimed-rewards.js?v=20261005-rested-operational';

initLoyaltyModalMotion();
const descriptions = new Map([
  ['Claimed rewards', 'Rewards you’ve claimed. Add them to your bag.'],
  ['Choose a reward', 'Exchange your points for a reward.'],
]);
let issuedVoucherAdapter;
const getIssuedVoucherAdapter = () => {
  issuedVoucherAdapter ||= createShopifyIssuedVoucherAdapter();
  return issuedVoucherAdapter;
};
const issuedVoucherApplyController = createIssuedVoucherApplyController({
  applyVoucher: (code) => getIssuedVoucherAdapter().apply(code),
  verifyVoucher: async (code) => {
    const cart = await getIssuedVoucherAdapter().refresh();
    return cart.discount_codes?.some((entry) => entry.applicable === true
      && typeof entry.code === 'string'
      && entry.code.toLocaleLowerCase('en-GB') === code.toLocaleLowerCase('en-GB')) === true;
  },
});
const issuedVoucherHistoryAuthorizer = createIssuedVoucherHistoryAuthorizer({
  authorizeCode: readReadyCashRewardByCode,
});
const reconcileIssuedVoucherCart = (event) => {
  issuedVoucherApplyController.reconcile(event.detail?.cart);
};
window.addEventListener('sigma:issued-voucher-cart-refresh', reconcileIssuedVoucherCart);
window.addEventListener('sigma:cart-drawer-page-section-update', reconcileIssuedVoucherCart);

function describeSections() {
  document.querySelectorAll('#loyaltylion .sigma-loyalty-page-title').forEach((title) => {
    if (title.textContent.trim() === 'Sigma Rewards Hub') title.textContent = 'Rewards Hub';
    title.parentElement?.classList.add('sigma-loyalty-page-intro');
  });
  describeMemberTierLabels();
  describeMemberGreeting();
  initLoyaltyBonusActivity();
  initLoyaltyFaqMotion();
  initLoyaltyClaimedRewards();
  document.querySelectorAll('.lion-integrated-page [role="region"], .lion-integrated-page section').forEach((section) => {
    const heading = section.querySelector(':scope > .lion-integrated-page-section__heading > .lion-integrated-page-section__heading-text');
    const title = heading?.textContent.trim();
    if (!descriptions.has(title)) return;
    if (section.getAttribute('aria-label') !== title) section.setAttribute('aria-label', title);
    const empty = section.querySelector('.lion-claimed-rewards-list--empty');
    const existing = section.querySelector(':scope > .sigma-rewards-section-description');
    if (empty) {
      existing?.remove();
      return;
    }
    if (existing) return;
    const description = document.createElement('p');
    description.className = 'sigma-rewards-section-description';
    description.textContent = descriptions.get(title);
    heading.parentElement.after(description);
  });
  stabilizeTierBadges();
  describeTierSymbols();
  formatTierBenefits();
  describeRewardTypes();
  describeCashVoucherExpiry();
  describeProductRewards();
  describeProductRewardImages();
  document.querySelectorAll('.lion-history-table').forEach((table) => {
    const labels = [...table.querySelectorAll('thead th')].map((cell) => cell.textContent.trim());
    table.querySelectorAll('tbody tr').forEach((row) => {
      [...row.cells].forEach((cell, index) => {
        if (labels[index] && cell.dataset.sigmaLabel !== labels[index]) cell.dataset.sigmaLabel = labels[index];
      });
    });
  });
}

// Keep provider-rendered tier names and availability intact; change labels only.
function describeMemberTierLabels() {
  const labels = [
    ['.sigma-loyalty-member-tier', /^\s*Membership:/, 'Current tier:'],
    ['.sigma-loyalty-member-next', /^\s*Next level:/, 'Next tier:'],
  ];
  labels.forEach(([selector, original, replacement]) => {
    document.querySelectorAll(`#loyaltylion ${selector}`).forEach((paragraph) => {
      paragraph.childNodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        const text = node.textContent.replace(original, replacement);
        if (text !== node.textContent) node.textContent = text;
      });
    });
  });
}

// Display labels only. Native tier identity and current-tier matching stay intact.
function describeTierSymbols() {
  const symbols = new Map([
    ['Alpha', 'α'], ['Beta', 'β'], ['Delta', 'Δ'], ['Sigma', 'Σ'], ['Omega', 'Ω'],
  ]);
  document.querySelectorAll(
    '#loyaltylion .lion-tier-progress__tier-name, .lion-integrated-page .sigma-loyalty-tier__name',
  ).forEach((label) => {
    const name = label.textContent.trim();
    const symbol = symbols.get(name);
    if (symbol) label.textContent = `${symbol} ${name}`;
  });
}

function readLoyaltyProgram() {
  try {
    return window.loyaltylion?.program || null;
  } catch {
    return null;
  }
}

// Mirror each cash offer's native provider duration without assuming a default.
function describeCashVoucherExpiry() {
  const program = readLoyaltyProgram();
  document.querySelectorAll(
    '.lion-integrated-page .lion-reward-item--cart-discount-voucher[data-reward-id]',
  ).forEach((card) => {
    const button = card.querySelector('.lion-reward-item__actions button');
    if (button && !button.classList.contains('button')) button.classList.add('button', 'button--primary');
    const copy = rewardExpiryCopy(program, card.getAttribute('data-reward-id'));
    let note = card.querySelector('.sigma-cash-voucher-expiry');
    if (!copy) {
      note?.remove();
      return;
    }
    if (!note) {
      const container = card.querySelector('.lion-reward-item__meta');
      if (!container) return;
      note = document.createElement('p');
      note.className = 'sigma-cash-voucher-expiry';
      container.append(note);
    }
    if (note.textContent !== copy) note.textContent = copy;
  });
}

// Reuse the bag drawer image preview, including its close motion and focus return.
function describeProductRewardImages() {
  const template = document.querySelector('#sigma-reward-image-expand');
  if (!(template instanceof HTMLTemplateElement)) return;
  document.querySelectorAll('.lion-integrated-page .lion-reward-item--product-cart').forEach((reward) => {
    const frame = reward.querySelector('.lion-reward-item__product-image');
    const image = frame?.querySelector('img');
    if (!image) return;
    frame.dataset.productDiscoveryImageSource = '';
    let button = frame.querySelector('[data-product-discovery-image-open]');
    if (!button) {
      button = template.content.firstElementChild.cloneNode(true);
      frame.append(button);
    }
    const title = reward.querySelector('.lion-reward-item__title')?.textContent.trim() || image.alt;
    const source = image.currentSrc || image.src;
    if (button.dataset.productDiscoveryImageSrc !== source) button.dataset.productDiscoveryImageSrc = source;
    if (button.dataset.productDiscoveryImageTitle !== title) button.dataset.productDiscoveryImageTitle = title;
    const label = `Expand image for ${title}`;
    if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  });
}

// Share the profile greeting without changing native balance or pending points.
// Late provider renders are refreshed by the shared greeting module too.
function describeMemberGreeting() {
  const page = document.querySelector('.lion-integrated-page');
  const greeting = page?.querySelector('.lion-header__welcome-message');
  const profile = document.querySelector('.site-header [data-profile-greeting]');
  const source = profile?.parentElement;
  if (!greeting || !source || greeting.querySelector('[data-profile-greeting]')) return;
  greeting.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
}

// Tier badges are small UI icons, including Omega below the initial viewport.
// Keep them out of the global photo reveal, which can leave a native lazy image
// clipped and transparent before the browser has selected its source.
function stabilizeTierBadges() {
  document.querySelectorAll('.lion-integrated-page .sigma-loyalty-tier__badge').forEach((badge) => {
    if (!(badge instanceof HTMLImageElement)) return;
    badge.dataset.imageLoading = 'off';
    badge.loading = 'eager';
    badge.removeAttribute('data-image-loading-state');
    badge.style.removeProperty('opacity');
    badge.style.removeProperty('filter');
    badge.style.removeProperty('clip-path');
    badge.style.removeProperty('-webkit-clip-path');
  });
}

// Label only provider-declared reward kinds. Costs and claim actions stay native.
function describeRewardTypes() {
  const types = [
    ['Free product', '.lion-reward-item--product-cart, .lion-icon__reward--product-cart'],
    ['Voucher', '.lion-reward-item--cart-discount-voucher, .lion-reward-item--product-discount-voucher, .lion-reward-item--collection-discount-voucher, .lion-reward-item--active-subscription-discount-voucher, .lion-icon__reward--cart-discount-voucher, .lion-icon__reward--product-discount-voucher, .lion-icon__reward--collection-discount-voucher, .lion-icon__reward--active-subscription-discount-voucher'],
  ];
  document.querySelectorAll('.lion-integrated-page .lion-reward-item, .lion-integrated-page .lion-claimed-reward-item').forEach((card) => {
    const type = types.find(([, selector]) => card.matches(selector) || card.querySelector(selector));
    if (!type) return;
    const claimed = card.classList.contains('lion-claimed-reward-item');
    const container = card.querySelector(claimed ? '.lion-claimed-reward-item__left' : '.lion-reward-item__meta');
    if (!container) return;
    let chip = container.querySelector('.sigma-reward-type');
    // Reuse the provider's Free product label rather than duplicate it.
    if (!chip && !claimed && type[0] === 'Free product') chip = container.querySelector('.lion-reward-item__percent-off');
    if (!chip) {
      chip = document.createElement('span');
      chip.className = 'sigma-reward-type';
      container.prepend(chip);
    } else if (!chip.classList.contains('sigma-reward-type')) {
      chip.classList.add('sigma-reward-type');
    }
    if (chip.textContent !== type[0]) chip.textContent = type[0];
  });
}

// Presentation only: use the same product action label throughout the catalogue.
// Never infer eligibility or replace the native product selection/claim action.
function describeProductRewards() {
  document.querySelectorAll('.lion-integrated-page .lion-rewards-list').forEach((list) => {
    list.querySelectorAll('.lion-reward-item--product-cart').forEach((card) => {
      const name = card.querySelector('.lion-reward-item__title')?.textContent.trim();
      const details = card.querySelector('.lion-reward-item__more-info-url a');
      if (name && details?.textContent.trim() === 'View product') {
        const label = `View product: ${name}`;
        if (details.getAttribute('aria-label') !== label) details.setAttribute('aria-label', label);
      }

      const button = card.querySelector('.lion-reward-item__actions button');
      if (button && !button.classList.contains('button')) button.classList.add('button', 'button--primary');
      const label = button?.querySelector('.lion-reward-item__redeem-button-text');
      const reason = label?.getAttribute('data-i18n-key');
      const hint = card.querySelector('.sigma-product-reward-shortfall');
      if (hint) {
        const descriptions = (button?.getAttribute('aria-describedby') || '').split(/\s+/).filter(value => value && value !== hint.id);
        if (button) {
          if (descriptions.length) button.setAttribute('aria-describedby', descriptions.join(' '));
          else button.removeAttribute('aria-describedby');
        }
        hint.remove();
      }
      if (label && (reason === 'ui.dashboard.redeem_points.more_points_needed' || reason === 'ui.dashboard.redeem_points.get_reward')) {
        if (label.textContent !== 'Choose options') label.textContent = 'Choose options';
      }
    });
  });
}

// Reformat only the existing published copy. Unknown/new provider copy stays intact.
function formatTierBenefits() {
  document.querySelectorAll('.lion-integrated-page p.sigma-loyalty-tier__benefit').forEach((paragraph) => {
    const copy = paragraph.textContent.trim();
    let items = copy.split(/\.\s+/).map((item) => item.replace(/\.$/, ''));
    const omegaBenefits = 'Includes complimentary Gold Cover for eligible machines, monthly giveaway entry, a reduced free-shipping threshold and access to exclusive Discord channels.';
    if (copy.endsWith(omegaBenefits)) {
      const context = document.createElement('p');
      context.className = 'sigma-loyalty-tier__context';
      context.textContent = copy.slice(0, -omegaBenefits.length).trim();
      paragraph.before(context);
      items = ['Complimentary Gold Cover for eligible machines', 'Monthly giveaway entry', 'Reduced free-shipping threshold', 'Access to exclusive Discord channels'];
    } else if (copy.includes('Includes ')) {
      return;
    }
    const list = document.createElement('ul');
    list.className = 'sigma-loyalty-tier__benefit sigma-loyalty-tier__benefits';
    for (const text of items.filter(Boolean)) {
      const item = document.createElement('li');
      item.textContent = text;
      list.append(item);
    }
    paragraph.replaceWith(list);
  });
}

describeSections();
const root = document.querySelector('#MainContent');
if (root) new MutationObserver(describeSections).observe(root, {
  childList: true, subtree: true, characterData: true, attributes: true,
  attributeFilter: ['aria-disabled', 'data-i18n-key'],
});

// Presentation only: retain provider nodes, eligibility and event handlers.
function describeClaimedRewards() {
  document.querySelectorAll('.lion-referral-widget-modal').forEach((modal) => {
    decorateRewardCopy(modal);
  });
  document.querySelectorAll('.lion-modal--rule-action').forEach((modal) => {
    decorateRewardClose(modal);
    decorateRewardCopy(modal);
    modal.querySelectorAll('.lion-action-button').forEach((button) => {
      if (!button.classList.contains('button')) button.classList.add('button', 'button--primary', 'lion-apply-reward-button');
    });
  });
  document.querySelectorAll('.lion-redeem-reward-modal').forEach((modal) => {
    const isProduct = modal.classList.contains('lion-redeem-reward-modal--product_cart');
    const title = modal.querySelector('.lion-modal__title');
    const claimed = describeRewardClaimSuccess(modal, isProduct);
    setRewardContext(title, claimed
      ? isProduct ? 'Product reward claimed' : 'Reward claimed'
      : isProduct ? 'Confirm product reward' : 'Confirm reward');
    decorateRewardClose(modal);
    const summary = modal.querySelector('.lion-redeem-reward-modal__confirm-summary');
    const match = summary?.textContent.trim().match(/^(?:Redeem|Exchange) ([\d,]+) points for this (?:reward|voucher)\?$/);
    const confirm = modal.querySelector('.lion-redeem-reward-modal__button--confirm');
    const cancel = modal.querySelector('.lion-redeem-reward-modal__button--cancel');
    if (cancel && !cancel.classList.contains('button')) cancel.classList.add('button');
    if (confirm && !confirm.classList.contains('button')) confirm.classList.add('button', 'button--primary', 'lion-apply-reward-button');
    if (isProduct && !claimed) describeProductConfirmation(modal, summary, confirm);
    if (!claimed && !isProduct && match && confirm?.firstChild?.nodeType === Node.TEXT_NODE &&
        ['Redeem', 'Claim voucher', 'Claim reward'].includes(confirm.textContent.trim())) {
      confirm.firstChild.data = `Claim for ${match[1]} points`;
    }
    if (!claimed && summary && modal.classList.contains('lion-redeem-reward-modal--cart_discount_voucher') && !modal.querySelector('.sigma-claim-note')) {
      const note = document.createElement('p');
      note.className = 'sigma-claim-note';
      note.textContent = 'Points are spent when you claim. Apply the reward to your bag now or save it for later.';
      summary.after(note);
    }
    // Shorten the provider's pre-claim copy without changing its duration.
    // Issued voucher dates and product reward copy remain native.
    if (!claimed && modal.classList.contains('lion-redeem-reward-modal--cart_discount_voucher')) {
      const expiry = modal.querySelector('.lion-redeem-reward-modal__expiration-message--pre-redeem');
      const duration = expiry?.textContent.trim()
        .match(/^Rewards expire (.+) after being issued\.$/)?.[1];
      if (duration) expiry.textContent = `Use within ${duration} of claiming.`;
    }
  });
  document.querySelectorAll('.lion-modal--history-info').forEach((modal) => {
    const content = modal.querySelector('.lion-history-info-modal-content');
    if (!content) return;
    const hasCode = Boolean(content.querySelector('.lion-reward-code'));
    const voided = Boolean(content.querySelector('.lion-history-state-bubble--void'));
    const refunded = voided &&
      /this reward has been refunded/i.test(content.querySelector('.lion-history-info-modal-content__comment')?.textContent || '');
    const contextLabel = refunded ? 'Refunded reward' : hasCode ? 'Claimed reward' : 'Points activity';
    setRewardContext(modal.querySelector('.lion-modal__title'), contextLabel);
    const apply = content.querySelector('.lion-apply-reward-button:not(.sigma-reward-apply)');
    if (apply && !apply.classList.contains('button')) apply.classList.add('button', 'button--primary');
    const issuedCode = content.querySelector('.lion-reward-code__code')?.textContent.trim();
    const issuedIdentity = issuedCode?.toLocaleLowerCase('en-GB');
    const ownedControl = content.querySelector('.sigma-reward-apply');
    const ownedActions = ownedControl?.parentElement;
    if (ownedActions && (voided || !issuedIdentity ||
        ownedActions.dataset.sigmaIssuedVoucherCode !== issuedIdentity)) {
      removeIssuedVoucherApplyAction(ownedActions);
    }
    if (!voided) decorateRewardApply(content);
    if (issuedCode && !voided && !content.querySelector('.sigma-reward-apply')) {
      issuedVoucherHistoryAuthorizer.check(modal, issuedCode, (reward) => {
        const currentCode = content.querySelector('.lion-reward-code__code')?.textContent.trim();
        const currentVoided = Boolean(content.querySelector('.lion-history-state-bubble--void'));
        if (!modal.isConnected || !content.isConnected || currentVoided || !currentCode ||
            currentCode.toLocaleLowerCase('en-GB') !== reward.receipt.code.toLocaleLowerCase('en-GB')) return;
        decorateRewardApply(content, {
          actionsClass: 'lion-history-info-modal-content__actions',
          allowCreate: true,
        });
      });
    }
    decorateRewardClose(modal);
    decorateRewardCopy(content);
    const points = content.querySelector('.lion-history-info-modal-content__points');
    const pointsText = points?.firstChild;
    const pointsMatch = pointsText?.nodeType === Node.TEXT_NODE && pointsText.data.trim().match(/^(?:(?:Originally claimed|Claimed) for )?(\d[\d,]* points?)$/);
    if (pointsMatch && (hasCode || refunded)) {
      const label = `${refunded ? 'Originally claimed' : 'Claimed'} for ${pointsMatch[1]}`;
      if (pointsText.data !== label) pointsText.data = label;
    }
    const status = content.querySelector('.lion-history-state-bubble--approved');
    const statusText = status?.firstChild;
    const canApply = content.classList.contains('lion-history-info-modal-content--can-be-applied');
    if (statusText?.nodeType === Node.TEXT_NODE) {
      const label = canApply ? 'Ready to use' : 'Approved';
      if (['Approved', 'Ready to use'].includes(statusText.data) && statusText.data !== label) statusText.data = label;
    }
  });
}

function setRewardContext(title, label) {
  if (!title) return;
  let context = title.querySelector('.sigma-reward-context');
  if (!context) {
    context = document.createElement('span');
    context.className = 'sigma-reward-context';
    title.prepend(context);
  }
  if (context.textContent !== label) context.textContent = label;
}

// Treat only provider-rendered claim output as success. Do not infer it from
// loading classes or provider state flags that can arrive before persistence.
function describeRewardClaimSuccess(modal, isProduct) {
  const issuedCode = modal.querySelector('.lion-reward-code');
  const code = issuedCode?.querySelector('.lion-reward-code__code')?.textContent.trim();
  const applied = modal.querySelector('.lion-redeem-reward-modal__applied-to-cart');
  if (!code && !applied) {
    confirmedClaimOutputs.delete(modal);
    if (modal.classList.contains('sigma-reward-claim-success')) modal.classList.remove('sigma-reward-claim-success');
    const previous = modal.querySelector('.sigma-reward-claim-success__content');
    if (previous) previous.classList.remove('sigma-reward-claim-success__content');
    return false;
  }

  // Refresh only after provider-rendered success, once per newly issued reward.
  // Opening an already claimed reward must not trigger another spend or check.
  const output = code || 'product-applied';
  if (confirmedClaimOutputs.get(modal) !== output) {
    confirmedClaimOutputs.set(modal, output);
    initLoyaltyBonusActivity()?.reconcile();
  }

  if (!modal.classList.contains('sigma-reward-claim-success')) modal.classList.add('sigma-reward-claim-success');
  const content = modal.querySelector('.lion-redeem-reward-modal__content');
  if (content && !content.classList.contains('sigma-reward-claim-success__content')) {
    content.classList.add('sigma-reward-claim-success__content');
  }
  const message = isProduct
    ? 'Your product reward is claimed.'
    : 'Your reward is saved in Claimed rewards.';
  // The SDK can restore this description after our introduction already exists,
  // including inline markup and line breaks. Keep cleanup idempotent on rerender.
  const legacyMessage = 'Your new voucher code is below. All your rewards can be viewed anytime on this page.';
  const normalize = (text) => text.replace(/\s+/g, ' ').trim();
  const legacyDescriptions = [...modal.querySelectorAll('p, div, span')].filter((element) =>
    !element.matches('.lion-modal, .lion-modal__content, .lion-redeem-reward-modal__content, .lion-redeem-reward-modal__confirm-box') &&
    !element.querySelector('button, a, input, select, .lion-reward-code') &&
    (normalize(element.textContent) === legacyMessage || normalize(element.innerText || '') === legacyMessage));
  let introduction = modal.querySelector('.sigma-reward-claim-success__message');
  if (!introduction) {
    const summary = modal.querySelector('.lion-redeem-reward-modal__confirm-summary');
    const oldIntroduction = legacyDescriptions[0];
    const summaryText = summary?.firstChild;
    if (oldIntroduction) {
      introduction = oldIntroduction;
    } else if (summaryText?.nodeType === Node.TEXT_NODE && summary.childNodes.length === 1) {
      introduction = summary;
    } else if (content) {
      introduction = document.createElement('p');
      if (summary) summary.before(introduction);
      else content.prepend(introduction);
    }
    if (introduction && !introduction.classList.contains('sigma-reward-claim-success__message')) {
      introduction.classList.add('sigma-reward-claim-success__message');
    }
  }
  if (introduction && introduction.textContent.trim() !== message) introduction.textContent = message;
  legacyDescriptions.forEach((description) => {
    if (description !== introduction && !introduction?.contains(description) &&
        !description.classList.contains('sigma-reward-claim-success__legacy-message')) {
      description.classList.add('sigma-reward-claim-success__legacy-message');
    }
  });
  modal.querySelectorAll('.sigma-claim-note').forEach((note) => note.remove());

  if (applied && !code) {
    const neutral = 'Check your bag before checkout.';
    const markerText = [...applied.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.data.trim());
    if (markerText && markerText.data.trim() !== neutral) markerText.data = neutral;
  }
  decorateRewardApply(content || modal, {
    allowCreate: Boolean(code && !isProduct &&
      modal.classList.contains('lion-redeem-reward-modal--cart_discount_voucher')),
  });
  decorateRewardCopy(content || modal);
  return true;
}

function decorateRewardCopy(content) {
  content.querySelectorAll('.lion-reward-code__clipboard-button, .lion-copy-to-clipboard-button').forEach((copy) => {
    const announcement = copy.parentElement?.querySelector('[aria-live]');
    if (!announcement || copyFeedback.has(copy)) return;
    let timer;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      if (!/^copied(?:\s+(?:to\s+)?(?:the\s+)?clipboard)?[.!]?$/i.test(announcement.textContent.trim())) {
        delete copy.dataset.sigmaCopied;
        return;
      }
      copy.dataset.sigmaCopied = 'true';
      timer = setTimeout(() => { delete copy.dataset.sigmaCopied; }, 2000);
    });
    observer.observe(announcement, { childList: true, subtree: true, characterData: true });
    copyFeedback.set(copy, observer);
  });
}

// Keep the provider selector, selected variant and claim lifecycle intact.
function describeProductConfirmation(modal, summary, confirm) {
  const points = summary?.textContent.trim().match(/^(?:(?:Redeem|Exchange) )?([\d,]+) points(?: for this (?:reward|voucher|product)\?)?$/)?.[1];
  const summaryText = summary?.firstChild;
  if (points && summaryText?.nodeType === Node.TEXT_NODE && summary.childNodes.length === 1) {
    const label = `${points} points`;
    if (summaryText.data !== label) summaryText.data = label;
  }
  const description = modal.querySelector('.lion-redeem-reward-modal__confirm-summary-extra');
  const descriptionText = description?.firstChild;
  if (descriptionText?.nodeType === Node.TEXT_NODE && description.childNodes.length === 1 &&
      ["We'll add this product to your cart for free and you can checkout as normal", 'This product will be added to your bag for free.'].includes(descriptionText.data.trim())) {
    const label = 'This product will be added to your bag for free.';
    if (descriptionText.data !== label) descriptionText.data = label;
  }
  const confirmText = confirm?.firstChild;
  if (points && confirm && !confirm.classList.contains('lion-action-button--loading') &&
      confirm.getAttribute('aria-busy') !== 'true' && confirmText?.nodeType === Node.TEXT_NODE &&
      /^(?:Redeem|Claim voucher|Claim reward|Claim for [\d,]+ points)$/.test(confirmText.data.trim())) {
    const label = `Claim for ${points} points`;
    if (confirmText.data !== label) confirmText.data = label;
  }
  const selector = modal.querySelector('.lion-redeem-reward-modal__free-product-variant-selector select');
  if (selector?.id) {
    let label = selector.parentElement.querySelector('.sigma-product-option-label');
    if (!label) {
      label = document.createElement('label');
      label.className = 'sigma-product-option-label';
      label.textContent = 'Choose an option';
      selector.before(label);
    }
    if (label.htmlFor !== selector.id) label.htmlFor = selector.id;
    if (['Product variant', 'Choose an option'].includes(selector.getAttribute('aria-label'))) {
      if (selector.getAttribute('aria-label') !== 'Choose an option') selector.setAttribute('aria-label', 'Choose an option');
    }
  }
}

// Issued voucher application is a cart operation, never another reward claim.
// The provider keeps ownership of seamless product claim actions.
function decorateRewardApply(content, {
  actionsClass = 'lion-redeem-reward-modal__button-container',
  allowCreate = false,
} = {}) {
  const scope = content.parentElement || content;
  const code = (content.querySelector('.lion-reward-code__code') ||
    scope.querySelector('.lion-reward-code__code'))?.textContent.trim();
  const nativeSelector = '.lion-apply-reward-button:not(.sigma-reward-apply):not(.lion-redeem-reward-modal__button--confirm):not(.lion-redeem-reward-modal__button--cancel)';
  const nativeCandidate = content.querySelector(nativeSelector) || scope.querySelector(nativeSelector);
  const appliedCandidate = content.querySelector('.lion-redeem-reward-modal__applied-to-cart') ||
    scope.querySelector('.lion-redeem-reward-modal__applied-to-cart');
  let actions = content.querySelector('.lion-history-info-modal-content__actions, .lion-redeem-reward-modal__button-container') ||
    scope.querySelector('.lion-history-info-modal-content__actions, .lion-redeem-reward-modal__button-container') ||
    nativeCandidate?.parentElement || appliedCandidate?.parentElement;
  const native = actions?.querySelector(nativeSelector);
  const applied = actions?.querySelector('.lion-redeem-reward-modal__applied-to-cart');
  if (!code) {
    if (!actions) return;
    actions.querySelector('.sigma-reward-apply')?.remove();
    actions.querySelector('.sigma-reward-apply-status')?.remove();
    if (actions.classList.contains('sigma-reward-apply-actions')) actions.classList.remove('sigma-reward-apply-actions');
    if (actions.dataset.sigmaCreatedApplyActions === 'true') actions.remove();
    return;
  }
  if (!actions && !allowCreate) return;
  if (actions && !allowCreate && !native && !applied) return;
  mountIssuedVoucherApplyAction({
    actions,
    actionsClass,
    allowCreate,
    appliedMarker: Boolean(applied),
    code,
    content,
    controller: issuedVoucherApplyController,
    nativeAction: native,
    onError: () => window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`),
  });
}

function decorateRewardClose(modal) {
  const close = modal.querySelector('.lion-modal__close-button');
  const template = document.querySelector('#sigma-reward-close-content');
  if (close && template && !close.classList.contains('site-close-button')) {
    close.classList.add('site-close-button');
    close.append(template.content.cloneNode(true));
  }
}

// LoyaltyLion creates separate page and portal roots with the same ID.
// Listen across both, but ignore mutations unrelated to provider content.
const copyFeedback = new WeakMap();
const confirmedClaimOutputs = new WeakMap();
describeClaimedRewards();
new MutationObserver((records) => {
  const changed = records.some(({ target, addedNodes }) => {
    const element = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;
    return element?.closest('#loyaltylion') || [...addedNodes].some((node) =>
      node.nodeType === Node.ELEMENT_NODE && (node.matches('#loyaltylion') || node.querySelector('#loyaltylion')));
  });
  if (changed) describeClaimedRewards();
}).observe(document.body, {
  childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'],
});
